import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { VOICE_PROFILE_URL } from '@utils/api/endpoints.voice-profiles.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import {
  AddVoiceSamplePayload,
  ReplaceVoiceSamplePayload,
  VoiceConsent,
  VoiceProfileSample
} from '@utils/types/organization/voice-profile.ts';
import { VOICE_PROFILE_QUERY_KEYS } from './query-keys.ts';

/** Consent headers the enrollment routes require on every write.
 *  The backend rejects the request outright without them, which is the point:
 *  a biometric sample cannot be stored without a recorded confirmation. */
const consentHeaders = (consent: VoiceConsent) => ({
  'Content-Type': 'audio/wav',
  'X-Voice-Consent-Confirmed': String(consent.confirmed),
  'X-Voice-Consent-Notice-Version': consent.noticeVersion
});

/** Append a sample, keeping the speaker's existing ones.
 *
 * `bytes` are the exact preview bytes the server cut for the dialog — the
 * browser neither downloads the recording nor encodes audio, and the server
 * does not cut it twice. Adding never destroys a previous sample; replacement
 * is a separate, explicitly targeted action.
 *
 * Mutation errors are reported by the caller, which renders the specific
 * actionable message (short selection, sample limit, missing media) in the
 * dialog. Supplying `onError` here replaces react-query's default handler, so
 * the generic global toast does not stack on top of it. */
export const useAddVoiceSample = () => {
  const queryClient = useQueryClient();
  return useMutation<
    VoiceProfileSample,
    AxiosError<BaseError>,
    AddVoiceSamplePayload
  >(
    async ({ speakerId, bytes, label, consent }) => {
      const { data } = await Axios.post<VoiceProfileSample>(
        VOICE_PROFILE_URL.addSample(speakerId, label),
        bytes,
        { headers: consentHeaders(consent) }
      );
      return data;
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(
          VOICE_PROFILE_QUERY_KEYS.samples(variables.speakerId)
        );
      },
      onError: () => {
        /* Surfaced in the dialog; see note above. */
      },
      useErrorBoundary: false
    }
  );
};

/** Replace one named sample. The route takes the exact `profileId` so the
 *  user is always told which sample is being overwritten, and the speaker's
 *  other samples are untouched. */
export const useReplaceVoiceSample = () => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError<BaseError>, ReplaceVoiceSamplePayload>(
    async ({ speakerId, profileId, bytes, label, consent }) => {
      await Axios.put(
        VOICE_PROFILE_URL.replaceSample(speakerId, profileId, label),
        bytes,
        { headers: consentHeaders(consent) }
      );
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(
          VOICE_PROFILE_QUERY_KEYS.samples(variables.speakerId)
        );
      },
      onError: () => {
        /* Surfaced in the dialog; see note above. */
      },
      useErrorBoundary: false
    }
  );
};
